import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert, Stack, Avatar,
  IconButton, Tooltip
} from '@mui/material';
import { Check, Close, Info, DateRange, Person } from '@mui/icons-material';
import api from '../../api/axios';
import { format, parseISO } from 'date-fns';

const LeaveApprovalManager = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/team/leave-requests');
      setRequests(res.data || []);
    } catch (err) {
      console.error('Failed to fetch leave requests:', err);
      setMessage({ type: 'error', text: 'Failed to load leave requests.' });
    }
    setLoading(false);
  };

  const handleOpenReview = (request) => {
    setSelectedRequest(request);
    setReviewNotes('');
    setReviewDialog(true);
  };

  const handleReview = async (status) => {
    try {
      await api.put(`/admin/team/leave-requests/${selectedRequest.id}`, {
        status,
        review_notes: reviewNotes
      });
      setMessage({ type: 'success', text: `Leave request ${status === 'approved' ? 'approved' : 'rejected'} successfully.` });
      setReviewDialog(false);
      fetchRequests();
    } catch (err) {
      console.error('Failed to review leave request:', err);
      setMessage({ type: 'error', text: 'Failed to update leave request.' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  if (loading && requests.length === 0) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  return (
    <Box>
      {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Pending Requests</Typography>
      {pendingRequests.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, mb: 4, bgcolor: '#F8FAFC' }}>
          <Typography color="text.secondary">No pending leave requests.</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0', mb: 4 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#FFFBEB' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingRequests.map((req) => (
                <TableRow key={req.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.light' }}>
                        {req.employee_name[0]}
                      </Avatar>
                      <Typography fontWeight={600}>{req.employee_name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={req.leave_type.toUpperCase()} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {format(parseISO(req.start_date), 'MMM d')} - {format(parseISO(req.end_date), 'MMM d')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button 
                      variant="contained" 
                      size="small" 
                      color="primary"
                      onClick={() => handleOpenReview(req)}
                      sx={{ borderRadius: 1.5, textTransform: 'none' }}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Request History</Typography>
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#F8FAFC' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reviewer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>No history found.</TableCell>
              </TableRow>
            ) : (
              processedRequests.map((req) => (
                <TableRow key={req.id} hover>
                  <TableCell fontWeight={500}>{req.employee_name}</TableCell>
                  <TableCell>
                    {format(parseISO(req.start_date), 'MMM d')} - {format(parseISO(req.end_date), 'MMM d')}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={req.status.toUpperCase()} 
                      color={getStatusColor(req.status)} 
                      size="small"
                      sx={{ fontWeight: 700, borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ display: 'block' }}>{req.reviewer_name || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{req.reviewed_at ? format(parseISO(req.reviewed_at), 'MMM d') : ''}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Review Leave Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Reason:</Typography>
                <Typography variant="body2">{selectedRequest.reason || 'No reason provided.'}</Typography>
              </Paper>
              <Alert severity="info" icon={<Info />} sx={{ borderRadius: 2 }}>
                Check the schedule for this period to see if any jobs need reassigning.
              </Alert>
              <TextField
                fullWidth
                label="Review Notes (optional)"
                multiline
                rows={3}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Explain approval or rejection reason..."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, justifyContent: 'space-between' }}>
          <Button onClick={() => setReviewDialog(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Stack direction="row" spacing={1}>
            <Button 
              variant="outlined" 
              color="error" 
              startIcon={<Close />}
              onClick={() => handleReview('rejected')}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Reject
            </Button>
            <Button 
              variant="contained" 
              color="success" 
              startIcon={<Check />}
              onClick={() => handleReview('approved')}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Approve
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveApprovalManager;
